import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Mic, Calendar, GraduationCap, Video, Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useLocation } from "react-router-dom";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

// Initialize Cal.com
declare global {
  interface Window {
    Cal?: any;
  }
}

const initCalEmbed = () => {
  if (typeof window !== 'undefined' && !window.Cal) {
    (function (C: any, A: string, L: string) {
      let p = function (a: any, ar: any) { a.q.push(ar); };
      let d = C.document;
      C.Cal = C.Cal || function () {
        let cal = C.Cal;
        let ar = arguments;
        if (!cal.loaded) {
          cal.ns = {};
          cal.q = cal.q || [];
          d.head.appendChild(d.createElement("script")).src = A;
          cal.loaded = true;
        }
        if (ar[0] === L) {
          const api = function () { p(api, arguments); };
          const namespace = ar[1];
          api.q = api.q || [];
          if (typeof namespace === "string") {
            cal.ns[namespace] = cal.ns[namespace] || api;
            p(cal.ns[namespace], ar);
            p(cal, ["initNamespace", namespace]);
          } else p(cal, ar);
          return;
        }
        p(cal, ar);
      };
    })(window, "https://app.cal.com/embed/embed.js", "init");

    window.Cal("init", "premier-contact-accompagnement-strategique", { origin: "https://app.cal.com" });
    window.Cal.ns["premier-contact-accompagnement-strategique"]("ui", { "hideEventTypeDetails": false, "layout": "month_view" });
  }
};

// Helper to extract YouTube video ID from various URL formats
const getYouTubeVideoId = (url: string) => {
  // Handle youtu.be format
  if (url.includes('youtu.be/')) {
    return url.split('youtu.be/')[1]?.split('?')[0];
  }
  // Handle youtube.com/shorts format
  if (url.includes('/shorts/')) {
    return url.split('/shorts/')[1]?.split('?')[0];
  }
  // Handle youtube.com/watch format
  if (url.includes('watch?v=')) {
    return url.split('watch?v=')[1]?.split('&')[0];
  }
  return null;
};

const getYouTubeThumbnail = (url: string, quality: 'maxres' | 'hq' | 'mq' | 'sd' = 'hq') => {
  const videoId = getYouTubeVideoId(url);
  const qualityMap = {
    maxres: 'maxresdefault',
    hq: 'hqdefault',
    mq: 'mqdefault', 
    sd: 'sddefault'
  };
  return `https://img.youtube.com/vi/${videoId}/${qualityMap[quality]}.jpg`;
};

const getEmbedUrl = (url: string) => {
  const videoId = getYouTubeVideoId(url);
  return `https://www.youtube.com/embed/${videoId}?autoplay=1&loop=1&playlist=${videoId}&controls=1`;
};

const services = [
  {
    id: "reels",
    icon: Play,
    title: "Reels",
    tagline: "Des vidéos qui arrêtent le scroll.",
    subtitle: "Créer des reels, ce n'est pas juste filmer avec son cell et publier n'importe quand.",
    description: "C'est savoir quoi dire, comment le dire… et comment captiver en 3 secondes.",
    highlight: "Chez Agence 8020, on crée des Reels pensés pour générer de la visibilité, des DM, des leads et du cashflow.",
    features: [
      { text: "Plan de contenu avec hooks puissants" },
      { text: "Tournage rapide et structuré" },
      { text: "Montage dynamique avec sous-titres stylisés" },
      { text: "Calendrier de publication optimisé" },
      { text: "Option repurposing de contenu existant" },
    ],
    videos: [
      { id: 1, youtubeUrl: "https://youtube.com/shorts/rTb5u8ADGyA", title: "Solemn - Réal Béland" },
      { id: 2, youtubeUrl: "https://youtube.com/shorts/8kK14DRXZOw", title: "MREX - Plus haute tour" },
      { id: 3, youtubeUrl: "https://youtube.com/shorts/tL0i9YTqPi0", title: "MREX - Olivier Lemieux" },
      { id: 4, youtubeUrl: "https://youtube.com/shorts/v1pNW2JvEbk", title: "Alex G - Nascar" },
    ],
  },
  {
    id: "podcasts",
    icon: Mic,
    title: "Podcasts",
    tagline: "Des conversations qui marquent les esprits.",
    subtitle: "Dans un monde où chaque seconde compte, les podcasts offrent un moyen puissant.",
    description: "Partager des histoires et des idées significatives qui résonnent.",
    highlight: "Notre équipe allie stratégie et storytelling créatif pour produire des podcasts qui informent, inspirent et construisent des connexions durables.",
    features: [
      { text: "Conception du format et identité audio" },
      { text: "Enregistrement studio professionnel" },
      { text: "Montage et post-production audio" },
      { text: "Distribution multi-plateformes" },
      { text: "Clips vidéo pour promotion sociale" },
    ],
    videos: [
      { id: 1, youtubeUrl: "https://www.youtube.com/watch?v=oEpS-W9tRpc", title: "Podcast Episode 1" },
      { id: 2, youtubeUrl: "https://www.youtube.com/watch?v=jgANL_902RA", title: "Podcast Episode 2" },
      { id: 3, youtubeUrl: "https://www.youtube.com/watch?v=cEcttA6d6aA", title: "Podcast Episode 3" },
      { id: 4, youtubeUrl: "https://www.youtube.com/watch?v=wW6u54nI87w", title: "Podcast Episode 4" },
      { id: 5, youtubeUrl: "https://www.youtube.com/watch?v=B3NGfkwTlto", title: "Podcast Episode 5" },
      { id: 6, youtubeUrl: "https://youtu.be/0WH7b5imWIA", title: "Podcast Episode 6" },
      { id: 7, youtubeUrl: "https://youtu.be/W5KF72dUzVQ", title: "Podcast Episode 7" },
      { id: 8, youtubeUrl: "https://youtu.be/Ec3hj028qvY", title: "Podcast Episode 8" },
      { id: 9, youtubeUrl: "https://youtu.be/niNdWhL092o", title: "Podcast Episode 9" },
    ],
  },
  {
    id: "events",
    icon: Calendar,
    title: "Événements",
    tagline: "Capturez l'énergie, racontez l'histoire.",
    subtitle: "Chaque événement mérite d'être raconté avec émotion et authenticité.",
    description: "Nous capturons les moments forts et les interactions qui rendent votre événement unique.",
    highlight: "Notre objectif : transformer vos moments en histoires mémorables qui continuent de rayonner bien après la fin de l'événement.",
    features: [
      { text: "Captation multi-caméras" },
      { text: "Récapitulatif dynamique" },
      { text: "Vidéo promotionnelle" },
      { text: "Interviews sur place" },
      { text: "Livraison express" },
    ],
    videos: [
      { id: 1, youtubeUrl: "https://youtube.com/shorts/UEoltm3MYUs", title: "Événement Short", format: "vertical" as const },
      { id: 2, youtubeUrl: "https://youtu.be/DBK_gulcMps", title: "Événement", format: "horizontal" as const },
    ],
  },
  {
    id: "formations",
    icon: GraduationCap,
    title: "Formations",
    tagline: "Transformez votre expertise en revenus récurrents.",
    subtitle: "Vous avez une expertise à transmettre? Transformez-la en une formation structurée, crédible et monétisable.",
    description: "Notre équipe vous accompagne à chaque étape : de la planification pédagogique jusqu'à la mise en ligne et la promotion.",
    highlight: "Que vous souhaitiez tourner dans notre studio ou dans votre propre environnement, nous vous accompagnons pour créer une formation de qualité qui génère des revenus récurrents.",
    features: [
      { text: "Accompagnement sur le format, le ton et la ligne éditoriale" },
      { text: "Captation (studio mobile ou en entreprise)" },
      { text: "Montage audio & vidéo professionnel" },
      { text: "Structuration pédagogique claire" },
      { text: "Intégration plateforme LMS" },
      { text: "Stratégie de lancement et promotion" },
    ],
    videos: [],
    customContent: true,
  },
  {
    id: "ads",
    icon: Video,
    title: "Publicités",
    tagline: "Des pubs qui convertissent, pas qui dérangent.",
    subtitle: "En plus de la création de contenu organique, nous produisons des publicités vidéo performantes.",
    description: "Pour Meta, YouTube et TikTok - des formats qui génèrent des résultats.",
    highlight: "Notre approche allie stratégie, storytelling et optimisation publicitaire, afin de transformer vos vidéos en véritables outils d'acquisition.",
    features: [
      { text: "Stratégie créative data-driven" },
      { text: "Production multi-formats" },
      { text: "A/B testing créatif" },
      { text: "Optimisation continue" },
      { text: "Reporting performance" },
    ],
    videos: [],
  },
];

const servicesSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": "Création de contenu vidéo B2B",
  "provider": {
    "@type": "LocalBusiness",
    "name": "Agence 8020",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Montréal",
      "addressRegion": "QC",
      "addressCountry": "CA"
    },
    "url": "https://agence8020.com"
  },
  "areaServed": {
    "@type": "Place",
    "name": "Québec, Canada"
  },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Services de création de contenu",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Création de Reels",
          "description": "Production de vidéos courtes pour réseaux sociaux qui captent l'attention et génèrent des leads"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Production de Podcasts",
          "description": "Conception, enregistrement et montage de podcasts professionnels pour entreprises B2B"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Captation d'événements",
          "description": "Captation vidéo multi-caméras pour événements corporatifs et conférences"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Formations en ligne",
          "description": "Production de formations vidéo professionnelles avec accompagnement pédagogique"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Publicités vidéo",
          "description": "Création de publicités vidéo performantes pour Meta, YouTube et TikTok"
        }
      }
    ]
  }
};

const ServicesPage = () => {
  const location = useLocation();
  const [activeService, setActiveService] = useState(services[0]);
  const [selectedVideo, setSelectedVideo] = useState<{ url: string; format: 'horizontal' | 'vertical' } | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    initCalEmbed();
  }, []);

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      const service = services.find(s => s.id === hash);
      if (service) {
        setActiveService(service);
      }
    }
  }, [location.hash]);

  return (
    <>
      <SEOHead
        title="Services de création de contenu vidéo B2B | Agence 8020 Montréal"
        description="Agence de création de contenu vidéo B2B à Montréal. Reels, podcasts, captation d'événements, formations en ligne et publicités vidéo pour générer des leads qualifiés."
        canonical="/services"
        schemaJson={servicesSchema}
      />
      <main className="min-h-screen bg-background overflow-hidden">
        <Navbar />
      
      <AnimatePresence mode="wait">
        <motion.div
          key={activeService.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Hero Section with Title */}
          <section className="pt-32 pb-8 relative">
            {/* Background Effects */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-gradient-to-b from-primary/20 to-transparent opacity-50 blur-[120px] rounded-full" />
            </div>
            
            <div className="container mx-auto px-6 relative z-10">
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="font-heading text-6xl md:text-8xl lg:text-9xl font-bold text-center tracking-tight"
              >
                {activeService.title}
              </motion.h1>
            </div>
          </section>

          {/* Navigation Tabs - Floating */}
          <div className="sticky top-20 z-40 py-4">
            <div className="container mx-auto px-6">
              <div className="flex justify-center">
                <div className="inline-flex gap-2 p-2 rounded-full bg-card/80 backdrop-blur-xl border border-border/50 shadow-xl">
                  {services.map((service) => (
                    <motion.button
                      key={service.id}
                      onClick={() => setActiveService(service)}
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                        activeService.id === service.id
                          ? "bg-primary text-primary-foreground shadow-lg"
                          : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <service.icon className="w-4 h-4" />
                      <span className="hidden sm:inline">{service.title}</span>
                    </motion.button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Video Carousel Section - Only show if there are videos */}
          {activeService.videos.length > 0 && (
            <section className="py-12">
              <div className="container mx-auto px-6">
                <Carousel
                  opts={{
                    align: "center",
                    loop: true,
                  }}
                  className="w-full"
                >
                  <div className="flex justify-end mb-6 gap-2">
                    <CarouselPrevious className="relative inset-0 translate-x-0 translate-y-0 h-12 w-12 rounded-full bg-primary/20 hover:bg-primary border-none text-foreground" />
                    <CarouselNext className="relative inset-0 translate-x-0 translate-y-0 h-12 w-12 rounded-full bg-primary/20 hover:bg-primary border-none text-foreground" />
                  </div>
                  <CarouselContent className="-ml-2 md:-ml-4">
                    {activeService.videos.map((video, index) => {
                      const isHorizontal = 'format' in video ? video.format === 'horizontal' : ['podcasts', 'events'].includes(activeService.id);
                      const isVertical = 'format' in video ? video.format === 'vertical' : !['podcasts', 'events'].includes(activeService.id);
                      return (
                      <CarouselItem key={video.id} className={`pl-2 md:pl-4 ${isHorizontal ? 'basis-[85%] sm:basis-[70%] md:basis-[480px]' : 'basis-[60%] sm:basis-[45%] md:basis-[300px]'}`}>
                        <motion.div
                          className={`relative ${isHorizontal ? 'aspect-video' : 'aspect-[9/16]'} rounded-3xl overflow-hidden cursor-pointer group`}
                          onMouseEnter={() => setHoveredIndex(index)}
                          onMouseLeave={() => setHoveredIndex(null)}
                          onClick={() => setSelectedVideo({ 
                            url: video.youtubeUrl, 
                            format: isHorizontal ? 'horizontal' : 'vertical' 
                          })}
                          whileHover={{ scale: 1.02, y: -8 }}
                        >
                          <img
                            src={getYouTubeThumbnail(video.youtubeUrl)}
                            alt={video.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              if (!target.src.includes('sddefault')) {
                                target.src = getYouTubeThumbnail(video.youtubeUrl, 'sd');
                              }
                            }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/40" />
                          
                          {/* Video info overlay */}
                          <div className="absolute top-4 left-4 right-4">
                            <div className="flex items-center gap-2 text-white/90">
                              <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                                <Play className="w-4 h-4 fill-white" />
                              </div>
                              <span className="text-sm font-medium line-clamp-1">{video.title}</span>
                            </div>
                          </div>
                          
                          {/* Play button overlay */}
                          <motion.div
                            className="absolute inset-0 flex items-center justify-center"
                            initial={{ opacity: 0.8 }}
                            animate={{ opacity: hoveredIndex === index ? 1 : 0.8 }}
                          >
                            <motion.div
                              className="w-20 h-20 rounded-full bg-[#FF0000] flex items-center justify-center shadow-2xl"
                              animate={{ 
                                scale: hoveredIndex === index ? 1.15 : 1,
                                boxShadow: hoveredIndex === index 
                                  ? "0 0 40px rgba(255,0,0,0.5)" 
                                  : "0 10px 30px rgba(0,0,0,0.3)"
                              }}
                              transition={{ duration: 0.3 }}
                            >
                              <Play className="w-8 h-8 text-white fill-white ml-1" />
                            </motion.div>
                          </motion.div>
                        </motion.div>
                      </CarouselItem>
                      );
                    })}
                  </CarouselContent>
                </Carousel>
              </div>
            </section>
          )}

          {/* Custom Content for Formations (no videos) */}
          {activeService.customContent && (
            <section className="py-12">
              <div className="container mx-auto px-6">
                <div className="max-w-4xl mx-auto">
                  <div className="grid md:grid-cols-2 gap-8 items-center">
                    <div className="rounded-3xl overflow-hidden bg-gradient-to-br from-primary/20 to-primary/5 p-8 aspect-video flex items-center justify-center">
                      <div className="text-center">
                        <GraduationCap className="w-16 h-16 text-primary mx-auto mb-4" />
                        <p className="text-lg text-muted-foreground">Formation sur mesure</p>
                      </div>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold mb-4">Formation adaptée à vos besoins</h2>
                      <p className="text-muted-foreground mb-4">
                        Que ce soit en studio ou dans vos locaux, nous créons des formations 
                        qui transmettent votre expertise de manière professionnelle et engageante.
                      </p>
                      <Button 
                        data-cal-link="maxim-rousseau-6ciolz/premier-contact-accompagnement-strategique"
                        data-cal-namespace="premier-contact-accompagnement-strategique"
                        data-cal-config='{"layout":"month_view"}'
                      >
                        Discuter de votre projet
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Content Section */}
          <section className="py-16">
            <div className="container mx-auto px-6">
              <div className="max-w-5xl mx-auto">
                <div className="grid lg:grid-cols-2 gap-12 items-start">
                  {/* Left - Description */}
                  <div>
                    <motion.h2
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                      className="font-heading text-4xl md:text-5xl font-bold mb-6"
                    >
                      {activeService.tagline}
                    </motion.h2>
                    
                    <motion.p
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.1 }}
                      className="text-lg text-muted-foreground mb-4"
                    >
                      {activeService.subtitle}
                    </motion.p>
                    
                    <motion.p
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.2 }}
                      className="text-lg text-muted-foreground mb-8"
                    >
                      {activeService.description}
                    </motion.p>

                    {/* Highlight box */}
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, delay: 0.3 }}
                      className="p-6 rounded-2xl bg-primary/10 border border-primary/20"
                    >
                      <div className="flex gap-4">
                        <Sparkles className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                        <p className="text-foreground">{activeService.highlight}</p>
                      </div>
                    </motion.div>
                  </div>

                  {/* Right - Features */}
                  <div>
                    <motion.h3
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                      className="text-xl font-semibold mb-6 text-muted-foreground"
                    >
                      Ce qui est inclus
                    </motion.h3>
                    
                    <div className="space-y-4">
                      {activeService.features.map((feature, index) => (
                        <motion.div
                          key={feature.text}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.4, delay: index * 0.08 }}
                          className="flex items-center gap-4 p-4 rounded-xl bg-card/50 border border-border/50 hover:border-primary/30 transition-all duration-300"
                        >
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                            <Check className="w-4 h-4 text-primary" />
                          </div>
                          <span className="text-foreground">{feature.text}</span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="max-w-3xl mx-auto text-center"
              >
                <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                  Prêt à transformer votre contenu en résultats?
                </h2>
                <p className="text-muted-foreground text-lg mb-8">
                  Réservez un appel stratégique gratuit pour discuter de votre projet.
                </p>
                <Button
                  size="lg"
                  className="h-14 px-8 text-lg"
                  data-cal-link="maxim-rousseau-6ciolz/premier-contact-accompagnement-strategique"
                  data-cal-namespace="premier-contact-accompagnement-strategique"
                  data-cal-config='{"layout":"month_view"}'
                >
                  Réserver un appel stratégique
                </Button>
              </motion.div>
            </div>
          </section>
        </motion.div>
      </AnimatePresence>

      {/* Video Modal */}
      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className={`p-0 bg-black border-none overflow-hidden rounded-3xl ${
          selectedVideo?.format === 'horizontal' 
            ? 'max-w-[900px] aspect-video' 
            : 'max-w-[400px] aspect-[9/16]'
        }`}>
          {selectedVideo && (
            <iframe
              src={getEmbedUrl(selectedVideo.url)}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )}
        </DialogContent>
      </Dialog>

      <Footer />
      </main>
    </>
  );
};

export default ServicesPage;